import React, { useState, useMemo } from 'react';
import { CompanyType } from '../../../../services/ConfigurationService/types/companyType';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { EditCompanyDialog } from '../dialogs/EditCompanyDialog';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';

interface CompanyGridRow {
  id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  contactEmail: string;
  phone: string;
}

export function CompanyGrid({ companies, onCompanySaved }: {
  companies: CompanyType[];
  onCompanySaved?: () => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<CompanyType | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Filter companies based on search term
  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) {
      return companies;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return companies.filter(company => 
      (company.name?.toLowerCase().includes(searchLower)) ||
      (company.id?.toLowerCase().includes(searchLower)) ||
      (company.street?.toLowerCase().includes(searchLower)) ||
      (company.city?.toLowerCase().includes(searchLower)) ||
      (company.state?.toLowerCase().includes(searchLower)) ||
      (company.contactEmail?.toLowerCase().includes(searchLower))
    );
  }, [companies, searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle row selection for editing
  const handleRowSelection = (args: RowSelectEventArgs) => {
    if (args.data) {
      const selectedRow = args.data as CompanyGridRow;
      const company = companies.find(c => c.id === selectedRow.id);
      if (company) {
        setSelectedCompany(company);
        setIsEditDialogOpen(true);
      }
    }
  };

  const handleEditDialogClose = () => {
    setSelectedCompany(null);
    setIsEditDialogOpen(false);
    if (onCompanySaved) {
      onCompanySaved();
    }
  };

  if (companies.length === 0) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by company name, ID, address, email..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No companies found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            No companies available
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle no search results
  if (filteredCompanies.length === 0 && searchTerm.trim()) {
    return (
      <Box>
        {/* Search Row */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SearchIcon sx={{ color: '#64748b' }} />
            <TextField
              fullWidth
              size="small"
              placeholder="Search by company name, ID, address, email..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  }
                }
              }}
            />
            {searchTerm && (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                sx={{ color: '#64748b' }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Box>
        
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
            No companies match your search
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            Try adjusting your search term: "{searchTerm}"
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Search Row */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by company name, ID, address, email..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#3b82f6',
                }
              }
            }}
          />
          {searchTerm && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              sx={{ color: '#64748b' }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* Grid */}
      <GridComponent
        dataSource={filteredCompanies}
        height={500}
        allowPaging={true}
        allowSorting={true}
        pageSettings={{ pageSize: 25 }}
        style={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff'
        }}
        enableHover={true}
        rowSelected={handleRowSelection}
      >
        <ColumnsDirective>
          <ColumnDirective 
            field="id" 
            headerText="ID" 
            width="120"
            textAlign="Left"
          />
          <ColumnDirective 
            field="name" 
            headerText="Name" 
            width="200"
            textAlign="Left"
          />
          <ColumnDirective 
            field="city" 
            headerText="City" 
            width="120"
            textAlign="Left"
          />
          <ColumnDirective 
            field="state" 
            headerText="State" 
            width="80"
            textAlign="Left"
          />
          <ColumnDirective 
            field="contactEmail" 
            headerText="Email" 
            width="200"
            textAlign="Left"
          />
          <ColumnDirective 
            field="phone" 
            headerText="Phone" 
            width="140"
            textAlign="Left"
          />
        </ColumnsDirective>
        <Inject services={[Page, Sort]} />
      </GridComponent>

      {/* Edit Dialog */}
      {selectedCompany && (
        <EditCompanyDialog 
          company={selectedCompany}
          open={isEditDialogOpen}
          onClose={handleEditDialogClose}
        />
      )}
    </Box>
  );
}
